/* eslint-disable react/no-array-index-key */
import {
  IGenericContent,
  IGenericInfoBlock,
  IGenericText,
} from '../../models/generic-info-block/generic-info-block';
import { FlexContainer } from '../flex-container/flex-container.styles';
import InputMessage from '../input-message/input-message.component';
import { SpinnerCircle } from '../spinner/spinner.styles';
import { HintItemContainer } from './hints.styles';
import {
  ContentContainer,
  MainBlockContainer,
  MainContentWrapper,
  SectionTitle,
  TextBlock,
  TextImage,
  TextItem,
  TextItemsContainer,
  TextNotice,
} from './styles';

const parseTextItem = (
  { type, content, style }: IGenericText,
  key?: number
) => {
  if (type === 'image') {
    return <TextImage src={content} />;
  }

  return (
    <TextItem
      key={key}
      $style={style[0]}>
      {content}
    </TextItem>
  );
};

type GenericInfoBlockMode = 'taxes' | 'non-compensated' | 'hints';

const renderNormalContent = (content: IGenericContent[]) =>
  content.map(({ texts, notice, general_style: generalStyle }, index) => {
    const isOnlyNotice = notice && texts.length === 0;

    return (
      <TextBlock
        $isOnlyNotice={!!isOnlyNotice}
        key={index}>
        {texts.length > 0 && (
          <TextItemsContainer $generalStyle={generalStyle?.at(0)}>
            {texts.map((textItem, innerIndex) =>
              parseTextItem(textItem, innerIndex)
            )}
          </TextItemsContainer>
        )}
        {notice && <TextNotice>{notice}</TextNotice>}
      </TextBlock>
    );
  });

const splitIntoHintGroups = (content: IGenericContent[]) => {
  const transformed: {
    titleItems: IGenericText[];
    descriptionItem: IGenericText;
  }[] = [];

  for (let i = 0; i < content.length; i += 2) {
    if (content[i + 1]) {
      transformed.push({
        titleItems: content[i].texts,
        descriptionItem: content[i + 1].texts[0],
      });
    }
  }

  return transformed;
};

const renderHintsContent = (content: IGenericContent[]) => {
  const hintItemsGrouped = splitIntoHintGroups(content);

  if (content.length % 2 !== 0) {
    return renderNormalContent(content);
  }

  return hintItemsGrouped.map(({ titleItems, descriptionItem }, index) => (
    <HintItemContainer key={index}>
      <FlexContainer
        $direction='column'
        $gap='8px'
        $align='flex-start'
        $justify='flex-start'>
        <FlexContainer
          $align='flex-start'
          $gap='4px'
          $justify='flex-start'>
          {titleItems.length === 2 && <TextImage src={titleItems[0].content} />}
          <p>{titleItems?.at(-1)?.content}</p>
        </FlexContainer>
        <small>{descriptionItem.content}</small>
      </FlexContainer>
    </HintItemContainer>
  ));
};

const renderContent = (
  mode: GenericInfoBlockMode,
  content: IGenericContent[]
) => {
  if (mode === 'hints') return renderHintsContent(content);

  return renderNormalContent(content);
};

interface Props {
  genericInfo: IGenericInfoBlock;
  mode: GenericInfoBlockMode;
  isLoading?: boolean;
}

const GenericInfoBlock = ({
  genericInfo: { main_block: mainBlock, general_notice: generalNotice },
  mode,
  isLoading,
}: Props) => (
  <MainBlockContainer $isEmpty={mainBlock.length === 0}>
    {isLoading && <SpinnerCircle />}
    {mainBlock.map(({ title, content }, outerIndex) => (
      <MainContentWrapper key={outerIndex}>
        {title && <SectionTitle>{title}</SectionTitle>}
        <ContentContainer>{renderContent(mode, content)}</ContentContainer>
      </MainContentWrapper>
    ))}
    {generalNotice && (
      <InputMessage
        hasIcon
        type='info'
        size='medium'>
        {generalNotice}
      </InputMessage>
    )}
  </MainBlockContainer>
);

export default GenericInfoBlock;
